import { Spinner } from '@frontend/components/common';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { UserSelectV2 } from '@frontend/user/select';
import { User } from '@frontend/user/utils';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import defaultLogo from '../../../assets/images/telloport_logo_horizontal.png';
import useBackupNotification from './backup-notification-user.controller';

const ID = 'backup-notification-user-page';

const BackupNotificationUserPage = () => {
    const viewProps = useBackupNotification();
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);

    useEffect(() => {
        if (viewProps.initialUser) setSelectedUser(viewProps.initialUser);
    }, [viewProps.initialUser]);

    const shell = (cardContent: ReactNode) => (
        <div
            id={ID}
            className='relative flex h-screen w-screen flex-col'>
            <div className='bg-brand-structure absolute top-0 right-0 left-0 -z-1 h-[40vh] w-full'></div>
            <div className='bg-background-surface mx-6 my-40 flex flex-col items-center gap-6 rounded-lg p-6 shadow-lg md:mx-auto md:w-1/2'>
                <img
                    src={logo}
                    className='img-fluid mx-4 my-12 w-44'
                    alt='Logo'
                />
                {cardContent}
            </div>
        </div>
    );

    if (viewProps.loadStatus === 'loading') {
        return shell(<Spinner />);
    }

    if (viewProps.loadStatus === 'error' || !viewProps.tokenUser) {
        return shell(
            <div className='flex w-full flex-col items-center gap-4'>
                <p className='text-foreground-default text-center'>{CommonMessage.ERRORS.FAILED_TO_LOAD}</p>
                <DefaultPrimaryButton
                    onClick={(e) => {
                        e?.preventDefault();
                        viewProps.retry();
                    }}
                    text={CommonMessage.BUTTONS.RETRY}
                />
            </div>
        );
    }

    return shell(
        <div className='flex w-full flex-col gap-6'>
            <div className='flex flex-col gap-2'>
                <h1 className='text-foreground-default text-2xl font-medium'>
                    <FormattedMessage
                        id='change-backup-notification-user.user-title'
                        description='Title on the backup notification user standalone page, including the current user display name'
                        defaultMessage='Change backup notification user for: {user}'
                        values={{ user: new User(viewProps.tokenUser).getDisplayName() }}
                    />
                </h1>
                <p className='text-foreground-default'>
                    <FormattedMessage
                        id='change-backup-notification-user.description'
                        description='Description explaining how to choose a backup notification user on the standalone page'
                        defaultMessage='Select a user who should receive backup notifications in case you are unable to pick-up parcels. This action update will be effective immediately.'
                    />
                </p>
            </div>

            <UserSelectV2
                value={selectedUser || undefined}
                onChange={(val) => setSelectedUser(val || null)}
                data-testid='backup-notification-user-select'
                disabled={!!viewProps.success}
            />

            <div>
                {viewProps.success ? (
                    <div className='rounded border border-green-200 bg-green-50 p-4 text-green-800'>
                        <FormattedMessage
                            id='change-backup-notification-user.success-message'
                            description='Success message shown after updating the backup notification user'
                            defaultMessage='Successfully updated backup notification user.'
                        />
                    </div>
                ) : (
                    <DefaultPrimaryButton
                        onClick={(e) => {
                            e?.preventDefault();
                            viewProps.confirm(selectedUser);
                        }}
                        text={CommonMessage.BUTTONS.SUBMIT}
                        disabled={!selectedUser || viewProps.saving}
                        isLoading={viewProps.saving}
                    />
                )}
            </div>
        </div>
    );
};

export default BackupNotificationUserPage;
